import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';

export default function CustomListsPage() {
  const { isSubscriber } = useAuth();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // id of list pending delete confirmation
  const [error, setError] = useState('');

  async function load() {
    try {
      const res = await fetch('/api/teacher/lists', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setLists(data.lists || []);
    } catch {
      setError('Could not load your lists. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Lists are a subscriber feature; non-subscribers only see the upgrade card
  useEffect(() => { if (isSubscriber) load(); }, [isSubscriber]);

  async function handleDelete(id) {
    const res = await fetch(`/api/teacher/lists/${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) {
      setConfirmDeleteId(null);
      setLists(prev => prev.filter(l => l.id !== id));
    } else {
      setError('Could not delete. Please try again.');
      setConfirmDeleteId(null);
    }
  }

  return (
    <DashboardLayout>
      <div style={s.header}>
        <h1 style={s.heading}>My Vocabulary Lists</h1>
        <Link to="/lists/new" style={s.newBtn}>+ New List</Link>
      </div>

      {/* Upgrade prompt for non-subscribers */}
      {!isSubscriber && (
        <div style={s.upgradeCard}>
          <div style={s.upgradeIcon}>📋</div>
          <div>
            <p style={s.upgradeHeading}>Custom lists are a subscriber feature</p>
            <p style={s.upgradeText}>
              Upload your own word/definition pairs and reuse them across game sessions.
              Subscribe for $19.99/year to unlock custom vocabulary uploads.
            </p>
            <Link to="/subscribe" style={s.upgradeBtn}>Subscribe for $19.99/yr →</Link>
          </div>
        </div>
      )}

      {isSubscriber && (
        <>
          {error && <p style={s.error}>{error}</p>}

          {loading ? (
            <p style={s.muted}>Loading…</p>
          ) : lists.length === 0 ? (
            <div style={s.emptyCard}>
              <p style={s.emptyText}>
                No custom lists yet.{' '}
                <Link to="/lists/new" style={s.inlineLink}>Create your first list →</Link>
              </p>
            </div>
          ) : (
            <div style={s.listGrid}>
              {lists.map(list => {
                const isPendingDelete = confirmDeleteId === list.id;
                return (
                  <div key={list.id} style={s.listCard}>
                    <div style={s.listInfo}>
                      <div style={s.listName}>{list.name}</div>
                      <div style={s.listMeta}>
                        {list.word_count} word{list.word_count !== 1 ? 's' : ''} · updated{' '}
                        {new Date(list.updated_at).toLocaleDateString()}
                      </div>
                    </div>

                    {isPendingDelete ? (
                      <div style={s.confirmRow}>
                        <span style={s.confirmText}>
                          Delete "{list.name}"? This cannot be undone.
                        </span>
                        <button onClick={() => handleDelete(list.id)} style={s.confirmDeleteBtn}>
                          Yes, delete
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)} style={s.confirmCancelBtn}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div style={s.listActions}>
                        <button
                          onClick={() => navigate('/game/new', { state: { customListId: list.id, customListName: list.name } })}
                          style={s.playBtn}
                        >
                          Use in game
                        </button>
                        <button
                          onClick={() => navigate(`/lists/${list.id}/edit`)}
                          style={s.actionBtn}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(list.id)}
                          style={{ ...s.actionBtn, color: '#dc2626' }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  heading: { fontSize: '28px', fontWeight: '700', color: 'var(--color-text)', margin: 0 },
  newBtn: { padding: '10px 20px', backgroundColor: 'var(--color-primary)', color: '#fff', borderRadius: '8px',
    fontWeight: '600', fontSize: '14px', textDecoration: 'none' },
  upgradeCard: { backgroundColor: 'var(--color-background)', border: '1px solid #e5e0d5', borderRadius: '12px',
    padding: '28px', display: 'flex', gap: '20px', alignItems: 'flex-start', marginBottom: '24px' },
  upgradeIcon: { fontSize: '32px', flexShrink: 0 },
  upgradeHeading: { fontWeight: '700', fontSize: '16px', color: 'var(--color-text)', margin: '0 0 6px' },
  upgradeText: { fontSize: '14px', color: '#6b6b7a', margin: '0 0 14px', lineHeight: '1.6' },
  upgradeBtn: { display: 'inline-block', padding: '10px 20px', backgroundColor: 'var(--color-primary)',
    color: '#fff', borderRadius: '8px', fontWeight: '600', fontSize: '14px', textDecoration: 'none' },
  error: { color: '#dc2626', fontSize: '14px', marginBottom: '16px' },
  muted: { color: '#6b6b7a', fontStyle: 'italic' },
  emptyCard: { backgroundColor: 'var(--color-background)', border: '1px solid #e5e0d5', borderRadius: '12px', padding: '32px' },
  emptyText: { color: '#6b6b7a', fontSize: '15px', margin: 0 },
  inlineLink: { color: 'var(--color-primary)', fontWeight: '600' },
  listGrid: { display: 'flex', flexDirection: 'column', gap: '12px' },
  listCard: { backgroundColor: 'var(--color-background)', border: '1px solid #e5e0d5', borderRadius: '10px',
    padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '16px', flexWrap: 'wrap' },
  listInfo: { flex: 1 },
  listName: { fontSize: '16px', fontWeight: '600', color: 'var(--color-text)', marginBottom: '4px' },
  listMeta: { fontSize: '13px', color: '#6b6b7a' },
  listActions: { display: 'flex', gap: '8px', alignItems: 'center' },
  playBtn: { padding: '8px 16px', backgroundColor: 'var(--color-primary)', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' },
  actionBtn: { background: 'none', border: 'none', fontSize: '13px', color: 'var(--color-primary)',
    cursor: 'pointer', fontWeight: '500', padding: '4px 8px' },
  confirmRow: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  confirmText: { fontSize: '13px', color: '#dc2626', fontWeight: '500' },
  confirmDeleteBtn: { padding: '7px 14px', backgroundColor: '#dc2626', color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  confirmCancelBtn: { padding: '7px 12px', background: 'none', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#6b6b7a' },
};
